import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, of, switchMap } from 'rxjs';
import {
  ORGANIZATION_REQUEST_TYPES,
  OrganizationRequest,
  OrganizationRequestType,
} from '../../../models/organization-request.model';
import { OrganizationRequestService } from '../../../services/organization-request.service';
import { AppState } from '../../../state/appstate';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';

/**
 * Request to create an organization. Only platform admins can create organizations: the user
 * describes the organization and attaches proof that it's registered (charity, religious body,
 * licensed business...). Once a platform admin approves, the organization appears in the user's
 * Management Groups with them as its admin.
 */
@Component({
  selector: 'app-org-request',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, BtnBackComponent],
  templateUrl: './org-request.component.html',
})
export class OrgRequestComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private store = inject(Store<AppState>);
  private requests = inject(OrganizationRequestService);
  private toastr = inject(ToastrService);
  private logger = inject(NGXLogger);
  private subs = new Subscription();

  readonly types = ORGANIZATION_REQUEST_TYPES;
  readonly maxDocuments = OrganizationRequestService.MAX_DOCUMENTS;
  readonly maxDocumentMb = OrganizationRequestService.MAX_DOCUMENT_BYTES / (1024 * 1024);

  uid: string | null = null;
  myRequests$: Observable<OrganizationRequest[]> = of([]);

  form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    purpose: ['', [Validators.required, Validators.maxLength(2000)]],
    orgType: ['' as OrganizationRequestType | '', Validators.required],
    registrationNumber: ['', [Validators.required, Validators.maxLength(100)]],
    country: ['', [Validators.required, Validators.maxLength(80)]],
    contactEmail: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
    contactPhone: ['', Validators.maxLength(40)],
  });

  files: File[] = [];
  fileError: string | null = null;
  submitting = false;
  submittedName: string | null = null;

  ngOnInit(): void {
    const user$ = this.store.select((s) => s.user?.user || null);
    this.subs.add(
      user$.subscribe((u) => {
        this.uid = u?.uid || null;
        if (u?.email && !this.form.controls.contactEmail.value) {
          this.form.controls.contactEmail.setValue(u.email);
        }
      })
    );
    this.myRequests$ = user$.pipe(switchMap((u) => (u?.uid ? this.requests.getMyRequests(u.uid) : of([]))));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files || []);
    input.value = '';
    this.logger.debug('org-request.component.ts :: Documents picked:', { count: picked.length });
    this.fileError = null;
    for (const file of picked) {
      if (!OrganizationRequestService.DOCUMENT_TYPES.includes(file.type)) {
        this.fileError = `"${file.name}" isn't a PDF or image.`;
        continue;
      }
      if (file.size > OrganizationRequestService.MAX_DOCUMENT_BYTES) {
        this.fileError = `"${file.name}" is larger than ${this.maxDocumentMb} MB.`;
        continue;
      }
      if (this.files.length >= this.maxDocuments) {
        this.fileError = `You can attach up to ${this.maxDocuments} documents.`;
        break;
      }
      this.files.push(file);
    }
  }

  removeFile(index: number): void {
    this.logger.debug('org-request.component.ts :: Remove document clicked:', { name: this.files[index]?.name });
    this.files.splice(index, 1);
  }

  async submit(): Promise<void> {
    this.logger.debug('org-request.component.ts :: Submit request clicked:', { name: this.form.controls.name.value });
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    if (this.files.length === 0) {
      this.fileError = 'Attach at least one document proving the organization is registered.';
      return;
    }
    if (!this.uid) return;

    this.submitting = true;
    const requestId = this.requests.newRequestId();
    const uploaded: string[] = [];
    try {
      for (const file of this.files) {
        uploaded.push(await this.requests.uploadDocument(this.uid, requestId, file));
      }
      const v = this.form.getRawValue();
      await this.requests.submitRequest({
        requestId,
        name: v.name.trim(),
        purpose: v.purpose.trim(),
        orgType: v.orgType as OrganizationRequestType,
        registrationNumber: v.registrationNumber.trim(),
        country: v.country.trim(),
        contactEmail: v.contactEmail.trim(),
        contactPhone: v.contactPhone.trim(),
      });
      this.logger.debug('org-request.component.ts :: Request submitted:', { requestId });
      this.submittedName = v.name.trim();
      this.form.reset({ contactEmail: v.contactEmail });
      this.files = [];
      this.toastr.success('Request sent. We will email you once it has been reviewed.');
    } catch (err: any) {
      this.logger.error('org-request.component.ts :: Submitting request failed:', { requestId, err });
      // Remove documents uploaded for a request that wasn't recorded
      await Promise.all(
        uploaded.map((p) =>
          this.requests.deleteDocument(p).catch((e) =>
            this.logger.error('org-request.component.ts :: Removing uploaded document failed:', { path: p, e })
          )
        )
      );
      this.toastr.error(err?.message || 'Could not send the request. Please try again.');
    } finally {
      this.submitting = false;
    }
  }

  typeLabel(value: string): string {
    return this.types.find((t) => t.value === value)?.label || value;
  }

  fileSize(bytes: number): string {
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
  }
}
